import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import BusinessDashboardNav from '../../components/ui/BusinessDashboardNav';
import Button from '../../components/ui/Button';
import EventDetailsTab from './components/EventDetailsTab';
import TicketingTab from './components/TicketingTab';
import MerchandiseTab from './components/MerchandiseTab';
import AnalyticsTab from './components/AnalyticsTab';
import { getAllEvents, createEvent, updateEvent, duplicateEvent } from '../../services/eventService';
import { getAllVenues } from '../../services/venueService';


const EventCreationManagementPage = () => {
  const [isNavCollapsed, setIsNavCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [events, setEvents] = useState([]);
  const [venues, setVenues] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  // Check if user has access to event management (admin or manager)
  useEffect(() => {
    if (userProfile && !['admin', 'manager']?.includes(userProfile?.role)) {
      navigate('/');
      return;
    }
  }, [userProfile, navigate]);

  const loadEvents = async () => {
    try {
      setLoading(true);
      const data = await getAllEvents({});
      setEvents(data || []);
    } catch (error) {
      setError(`Failed to load events: ${error?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const loadVenues = async () => {
    try {
      const data = await getAllVenues({});
      setVenues(data || []);
    } catch (error) {
      console.error('Failed to load venues:', error);
    }
  };

  useEffect(() => {
    loadEvents();
    loadVenues();
  }, []);

  const tabs = [
    { id: 'details', label: 'Event Details', icon: '📝' },
    { id: 'ticketing', label: 'Ticketing', icon: '🎫' },
    { id: 'merchandise', label: 'Merchandise', icon: '🛍️' },
    { id: 'analytics', label: 'Analytics', icon: '📊' }
  ];

  const handleCreateNewEvent = () => {
    setSelectedEvent(null);
    setActiveTab('details');
    setError('');
    setSuccess('');
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setActiveTab('details');
    setError('');
    setSuccess('');
  };

  const handleSaveEvent = async (eventData) => {
    try {
      setError('');
      setLoading(true);

      let savedEvent;
      if (selectedEvent?.id) {
        savedEvent = await updateEvent(selectedEvent?.id, eventData);
        setSuccess('Event updated successfully');
      } else {
        savedEvent = await createEvent(eventData);
        setSuccess('Event created successfully');
      }

      setSelectedEvent(savedEvent);
      loadEvents();
    } catch (error) {
      setError(`Failed to save event: ${error?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicateEvent = async (eventId) => {
    try {
      setError('');
      setLoading(true);
      
      const duplicatedEvent = await duplicateEvent(eventId);
      setSelectedEvent(duplicatedEvent);
      setSuccess('Event duplicated successfully');
      loadEvents();
    } catch (error) {
      setError(`Failed to duplicate event: ${error?.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userRole');
    navigate('/login');
  };

  if (!['admin', 'manager']?.includes(userProfile?.role)) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background">
      <BusinessDashboardNav
        userRole={userProfile?.role || 'manager'}
        isCollapsed={isNavCollapsed}
        onToggleCollapse={() => setIsNavCollapsed(!isNavCollapsed)}
        user={{
          name: userProfile?.full_name || "Event Manager",
          role: userProfile?.role === 'admin' ? "Platform Administrator" : "Event Manager"
        }}
      />
      <div className={`transition-all duration-300 ${isNavCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        <header className="bg-card border-b border-border shadow-sm px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Event Creation & Management</h1>
              <p className="text-sm text-muted-foreground">
                Comprehensive event lifecycle management with creation wizard and analytics
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="default"
                size="sm"
                iconName="Plus"
                onClick={handleCreateNewEvent}
              >
                New Event
              </Button>
              <Button
                variant="outline"
                size="sm"
                iconName="LogOut"
                onClick={handleLogout}
              >
                Sign Out
              </Button>
            </div>
          </div>
        </header>

        <main className="flex h-[calc(100vh-140px)]">
          {/* Left Sidebar - Event List */}
          <div className="w-80 border-r border-border flex flex-col">
            <div className="p-4 border-b border-border">
              <h3 className="font-medium text-foreground mb-3">Events</h3>
              {loading && (
                <div className="text-sm text-muted-foreground">Loading events...</div>
              )}
            </div>
            
            <div className="flex-1 overflow-y-auto">
              <div className="p-4 space-y-3">
                {events?.map(event => (
                  <div
                    key={event?.id}
                    onClick={() => handleSelectEvent(event)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedEvent?.id === event?.id
                        ? 'border-primary bg-primary/5' :'border-border hover:border-muted-foreground'
                    }`}
                  >
                    <div className="font-medium text-foreground text-sm mb-1">
                      {event?.title}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(event?.event_date)?.toLocaleDateString()}
                    </div>
                    <div className={`text-xs mt-1 px-2 py-1 rounded-full inline-block ${
                      event?.status === 'published' ?'bg-green-100 text-green-800'
                        : event?.status === 'draft' ?'bg-yellow-100 text-yellow-800' :'bg-gray-100 text-gray-800'
                    }`}>
                      {event?.status}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 flex flex-col">
            {selectedEvent || activeTab === 'details' ? (
              <>
                {/* Tab Navigation */}
                <div className="border-b border-border">
                  <div className="flex">
                    {tabs?.map(tab => (
                      <button
                        key={tab?.id}
                        onClick={() => setActiveTab(tab?.id)}
                        className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                          activeTab === tab?.id
                            ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
                        }`}
                      >
                        <span className="mr-2">{tab?.icon}</span>
                        {tab?.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Tab Content */}
                <div className="flex-1 overflow-y-auto">
                  {error && (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mx-6 mt-4 rounded">
                      {error}
                    </div>
                  )}
                  
                  {success && (
                    <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 mx-6 mt-4 rounded">
                      {success}
                    </div>
                  )}

                  {activeTab === 'details' && (
                    <EventDetailsTab
                      event={selectedEvent}
                      venues={venues}
                      onSave={handleSaveEvent}
                      onDuplicate={handleDuplicateEvent}
                      loading={loading}
                    />
                  )}

                  {activeTab === 'ticketing' && (
                    <TicketingTab
                      event={selectedEvent}
                      onSave={handleSaveEvent}
                    />
                  )}

                  {activeTab === 'merchandise' && (
                    <MerchandiseTab
                      event={selectedEvent}
                    />
                  )}

                  {activeTab === 'analytics' && (
                    <AnalyticsTab
                      event={selectedEvent}
                    />
                  )}
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl text-muted-foreground">🎪</span>
                  </div>
                  <h3 className="text-lg font-medium text-foreground mb-2">
                    No Event Selected
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Select an event from the list or create a new one to get started
                  </p>
                  <Button
                    variant="default"
                    onClick={handleCreateNewEvent}
                  >
                    Create New Event
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default EventCreationManagementPage;